import { GameLoader } from "../loaders/GameLoader";
import { AliceIQService } from "./AliceIQService";
import { SentryUtils } from "../../utils/sentry.utils";
import { GameReloadHandler } from "../handlers/GameReloadHandler";
import { TypeInitParams } from "../types/TypeInitParams";
import { TypeEnvConfig } from "../../utils/env.utils";

export class CommunicationService {
    private _aliceIQService: AliceIQService | null = null;

    constructor(
        private gameLoader: GameLoader,
        private iframe: HTMLIFrameElement,
        private reloadHandler: GameReloadHandler,
        private initParams: TypeInitParams,
        private envConfig?: TypeEnvConfig,
    ) {
        window.addEventListener("send-replay-open", () =>
            this.sendGameAutoplayStop("open replay")
        );
        window.addEventListener("send-lobby-open", () =>
            this.sendGameAutoplayStop("open lobby")
        );
        window.addEventListener("shell-feature-content-visible", (event: CustomEvent<boolean>) =>
            this.sendGameModalVisible(event.detail)
        );

        this.subscribe();
        this.initAliceIQ();
    }

    private initAliceIQ(): void {
        if (!this.envConfig?.aliceIQ || !this.envConfig.aliceIQ.url || !this.envConfig.aliceIQ.enable) {
            console.log("[Bash] AliceIQ disabled: no aliceIQ config resolved from env.");
            return;
        }

        this._aliceIQService = new AliceIQService(this.envConfig.aliceIQ);
        this._aliceIQService.load();
    }

    public sendGameAutoplayStop(action: string): void {
        if (!this.isIframeAvailable()) {
            return;
        }

        console.log &&
            console.log(
                `Bash action "${action}" to game -> [game:autoplay:stop]`,
            );
        this.iframe?.contentWindow?.postMessage(
            { name: "[game:autoplay:stop]" },
            "*"
        );
    }

    private sendGameModalVisible(visible: boolean): void {
        if (!this.isIframeAvailable()) {
            return;
        }

        console.log &&
        console.log(
            `Bash modal active "${visible ? 'open' : 'hide'}" to game -> [shell:modal:active]`, { visible }
        );
        this.iframe?.contentWindow?.postMessage(
            { name: "[shell:modal:active]", visible },
            "*"
        );
    }

    private isIframeAvailable(): boolean {
        return !!(this.iframe && this.iframe.contentWindow);
    }

    private subscribe(): void {
        window.addEventListener("message", (message: MessageEvent) => {
            if (!this.iframe || !message.data) {
                return;
            }

            try {
                if (message.data.name === "[game::reload]") {
                    this.reloadHandler.handleReload();
                    return;
                }

                if (message.data.name === "[replay::finished]") {
                    window.dispatchEvent(
                        new CustomEvent<void>("event-replay-finished")
                    );
                    return;
                }

                if (message.data.name === "[popup.update]") {
                    console.log(
                        `[Updated game popup state "${message.data.active}"]`
                    );
                    this.gameLoader.setActiveGamePopup(message.data.active);
                    return;
                }

                if (message.data.name === "[game_loaded]") {
                   void SentryUtils.trackGameLoadMetric();
                }

                if (message.data.name === "[shell-feature]") {
                    this.gameLoader.setShellFeatureVisible(message.data.visible);
                    return;
                }

                if (message.data.name === "[connect]") {
                    const userId = String(message.data.userId || "").trim();

                    if (userId && userId !== "-1") {
                        this.gameLoader.setUserId(userId);
                    }

                    if (message.data.clientSessionId) {
                        const sessionId = String(message.data.clientSessionId);
                        this.gameLoader.setSessionId(sessionId);
                    }

                    this.gameLoader.setShellFeatureVisible(true);

                    if (!userId || userId === "-1") {
                        return;
                    }

                    const { hash_wlid } = message.data;
                    const { gameName } = this.initParams;

                    const wlCode = this.gameLoader.global?.configs?.partner;
                    this._aliceIQService?.setConfig({
                        wlCode: wlCode,
                        walletId: hash_wlid || userId,
                        gameName,
                    });
                    this._aliceIQService?.subscribe((config) => {
                        void SentryUtils.trackDevicePlayerInfo(
                            config.visitorId ?? "unknown"
                        );
                    });

                    return;
                }

                this.iframe?.contentWindow?.postMessage(message.data, "*");
            } catch (ep: any) {
                console.warn(`PostMessage transfer unavailable: ${ep.message}`);
            }
        });
    }
}
